// DataDisplay wrapper utility — mirrors the datasource wrapper pattern in datasource.utils.ts.
import type { AstNode, AstGroup, DataDisplaySpec } from '../types';
import type { DataDisplayTemplate } from './datadisplay.types';
import { renderDataDisplayFromTemplate } from './datadisplay.template-renderer';
import { buildRenderContext } from '../render-kit';
import { toArray } from '../types';
import { SupportedTools } from '../../../interfaces';
import { resolveDataDisplay } from './datadisplay.resolve';

/**
 * Wrap a renderer so it auto-appends a DataDisplay clause when a dataDisplay directive is present.
 *
 * Analogous to wrapRendererWithDatasource — this is the outermost wrapper in the chain:
 *   wrapRendererWithDataDisplay(wrapRendererWithDatasource(_base, ...), tool, template)
 *
 * Behavior when no dataDisplay directive and no datasource category:  returns body unchanged.
 * Preserves helpText, datasourceHelpText, and dataDisplayHelpText from the inner renderer.
 */
export function wrapRendererWithDataDisplay<R extends (ast: AstNode) => string>(
  base: R,
  tool: SupportedTools,
  template: DataDisplayTemplate,
): R {
  const wrapped = ((ast: AstNode): string => {
    const body = base(ast);

    const root = ast.kind === 'group' ? (ast as AstGroup) : undefined;
    const ddSpec: DataDisplaySpec | undefined = root?.directives?.dataDisplay;
    const fallbackCats = toArray(root?.directives?.datasource?.category);

    // Nothing to do if no dataDisplay spec and no fallback categories for defaults
    if (!ddSpec && !fallbackCats.length) return body;

    const resolved = resolveDataDisplay(
      ddSpec ?? {},
      fallbackCats.length ? fallbackCats : undefined,
      tool, // enables applying tool-specific overrides to the datadisplay defaults
    );

    // Nothing to render if resolution produced no fields, groupBy, or aggFields
    if (!resolved.fields.length && !resolved.groupBy.length && !resolved.aggFields.length) {
      return body;
    }

    const ctx = buildRenderContext(tool, undefined, fallbackCats[0]);
    const ddClause = renderDataDisplayFromTemplate(resolved, ctx, template);
    return ddClause ? template.join(body, ddClause) : body;
  }) as R;

  // Preserve all help text properties from the inner renderer
  (wrapped as any).helpText = (base as any).helpText;
  (wrapped as any).datasourceHelpText = (base as any).datasourceHelpText;
  (wrapped as any).dataDisplayHelpText = (base as any).dataDisplayHelpText;
  (wrapped as any).makeComment = (base as any).makeComment;
  return wrapped;
}

/**
 * Render a DataDisplay clause in isolation — no filter body, no datasource.
 * Useful for previewing the display clause or testing a spec against a tool.
 */
export function renderDataDisplayStandalone(
  spec: DataDisplaySpec,
  tool: SupportedTools,
  template: DataDisplayTemplate,
  fallbackCategories?: string[],
): string {
  const resolved = resolveDataDisplay(spec, fallbackCategories, tool);
  const ctx = buildRenderContext(tool);
  return renderDataDisplayFromTemplate(resolved, ctx, template);
}
